import { Component, OnInit, Inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatAngularModule } from '../../shared/modules/mat-angular.module';
import { CitationCardComponent } from '../../shared/components/citation-card/citation-card.component';
import { PromptTrackingService, PromptDetail } from '../../services/prompt-tracking.service';
import { CountryService } from '../../services/country.service';

export interface PromptDetailDialogData {
  promptId: string;
  projectId?: string | null;
}

@Component({
  selector: 'app-prompt-detail-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatAngularModule,
    CitationCardComponent
  ],
  templateUrl: './prompt-detail-dialog.component.html',
  styleUrl: './prompt-detail-dialog.component.scss'
})
export class PromptDetailDialogComponent implements OnInit {
  loading = false;
  error: string | null = null;

  promptDetail: PromptDetail | null = null;
  
  // Location and date filters
  location: string = 'United States';
  selectedDate: string = '';
  
  // Country options
  countryOptions: string[] = [];

  constructor(
    public dialogRef: MatDialogRef<PromptDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PromptDetailDialogData,
    private promptTrackingService: PromptTrackingService,
    private countryService: CountryService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.countryService.getCountryOptions().subscribe(options => {
      this.countryOptions = options;
      this.cdr.detectChanges();
    });
    this.loadPromptDetail();
  }

  loadPromptDetail(): void {
    this.loading = true;
    this.error = null;

    this.promptTrackingService.getPromptDetail(
      this.data.promptId
    ).subscribe({
      next: (detail) => {
        this.promptDetail = detail;
        this.selectedDate = this.formatDateForDisplay(detail.snapshot_timestamp);
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading prompt detail:', err);
        this.error = err.error?.detail || 'Failed to load prompt details';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }




  formatDateForDisplay(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatTimestamp(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatAIContent(content: string): string {
    // Format the AI content with line breaks
    return content.split('\n\n').map(paragraph => {
      return `<p>${paragraph.replace(/\n/g, '<br>')}</p>`;
    }).join('');
  }

  formatCitations(citations: Array<{ url: string; title: string }>): any[] {
    return citations.map((citation, index) => {
      let domain = '';
      try {
        domain = new URL(citation.url).hostname;
      } catch {
        // If URL parsing fails, extract domain manually
        const match = citation.url.match(/https?:\/\/([^\/]+)/);
        domain = match ? match[1] : citation.url;
      }
      
      return {
        id: index + 1,
        title: citation.title || citation.url,
        url: citation.url,
        snippet: '',
        domain: domain,
        confidence: 1.0
      };
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
